import React from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import QuestionMarkOutlinedIcon from "@mui/icons-material/QuestionMarkOutlined";
import { commonStyles } from "../commonStyles";

// Shared confirm / destructive / success dialog (Phase A promotion, module 11
// polish pass). Donor skins:
// - "lg" (default): ManageInvoiceActionModals 560px shell (confirm at
//   1181-1242, success at 1244-1270 / InvoiceSuccessDialog) and the
//   memberWorkflowShared delete-logo confirm. Donor hexes are normative.
// - "sm": eventSalesShared ConfirmDialog / ResultModal xs shell (h-16 circle,
//   sentence-case actions).
// Built on MUI Dialog to keep focus-trap/backdrop parity with every donor.
// Phase A creates the component only — existing copy-pasted dialogs are NOT
// rewired until Phase B.

export type ConfirmDialogVariant = "destructive" | "confirm" | "success";

export type ConfirmDialogConfirmColor = "red" | "blue" | "gold" | "green";

export type ConfirmDialogProps = {
  open: boolean;
  variant: ConfirmDialogVariant;
  /** Centered extrabold heading */
  title: string;
  /** Optional supporting paragraph under the title */
  message?: React.ReactNode;
  /**
   * Icon override; defaults: destructive/confirm -> QuestionMarkOutlinedIcon,
   * success -> CheckCircleOutlineIcon. Destructive delete flows pass
   * DeleteOutlineIcon.
   */
  icon?: React.ReactNode;
  /** default "lg" (560px shell, h-28 circle); "sm" = eventSales xs shell, h-16 circle */
  size?: "sm" | "lg";
  /** Additive donor skin for the Manage Your Chamber success dialog. */
  appearance?: "default" | "adminChamberSuccess";
  /** default "Proceed"; ignored for success */
  confirmLabel?: string;
  /** default "Cancel"; ignored for success */
  cancelLabel?: string;
  /** Hides the secondary Cancel action when the donor dialog closes by X only. */
  hideCancel?: boolean;
  /** default false. TRUE renders PROCEED/CANCEL uppercase — uppercase ONLY via this prop */
  uppercaseActions?: boolean;
  /** defaults: destructive -> "red", confirm -> "blue" */
  confirmColor?: ConfirmDialogConfirmColor;
  /** Disables both buttons; spinner in confirm button (CircularProgress size 16, color inherit) */
  loading?: boolean;
  /** e.g. 2000 to sit above fullscreen dialogs (applied to Dialog sx) */
  zIndex?: number;
  /** Required unless variant="success" */
  onConfirm?: () => void;
  /** Red circular X, backdrop, and Cancel all call this */
  onClose: () => void;
};

type ButtonPalette = { bg: string; hover: string };

// Normative lg hexes per the Phase A contract (invoice donors); sm keeps the
// eventSalesShared in-family values so later migrations stay pixel-faithful.
const lgConfirmPalettes: Record<ConfirmDialogConfirmColor, ButtonPalette> = {
  red: { bg: "#F31723", hover: "#D90F1A" },
  blue: { bg: "#1684FF", hover: "#0B74D5" },
  gold: { bg: commonStyles.cpaGold, hover: commonStyles.cpaGoldDark },
  green: { bg: "#16B983", hover: "#0E9F70" },
};

const smConfirmPalettes: Record<ConfirmDialogConfirmColor, ButtonPalette> = {
  red: { bg: "#ef4444", hover: "#dc2626" },
  blue: { bg: "#2563eb", hover: "#1d4ed8" },
  gold: { bg: commonStyles.cpaGold, hover: commonStyles.cpaGoldDark },
  green: { bg: "#16B983", hover: "#0E9F70" },
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  variant,
  title,
  message,
  icon,
  size = "lg",
  appearance = "default",
  confirmLabel = "Proceed",
  cancelLabel = "Cancel",
  hideCancel = false,
  uppercaseActions = false,
  confirmColor,
  loading = false,
  zIndex,
  onConfirm,
  onClose,
}) => {
  const isSuccess = variant === "success";
  const resolvedConfirmColor: ConfirmDialogConfirmColor =
    confirmColor ?? (variant === "destructive" ? "red" : "blue");
  const dialogSx = zIndex === undefined ? undefined : { zIndex };

  const defaultIcon = isSuccess ? (
    <CheckCircleOutlineIcon sx={{ fontSize: size === "lg" ? 64 : 40 }} />
  ) : (
    <QuestionMarkOutlinedIcon sx={{ fontSize: size === "lg" ? 56 : 36 }} />
  );
  const renderedIcon = icon ?? defaultIcon;

  if (isSuccess && appearance === "adminChamberSuccess") {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        sx={dialogSx}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            maxWidth: "505px",
            overflow: "visible",
            width: "100%",
          },
        }}
      >
        <div className="relative px-[18px] py-6 text-center min-[601px]:p-8">
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              backgroundColor: "#ED1C24",
              boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
              color: "white",
              height: 30,
              padding: 0,
              position: "absolute",
              right: 8,
              top: 8,
              width: 30,
              "&:hover": { backgroundColor: "#D41920" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <div className="mx-auto mb-4 flex h-[105px] w-[105px] items-center justify-center rounded-full border border-white bg-[#00B621]">
            <svg
              width="60"
              height="60"
              viewBox="0 0 60 60"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="30"
                cy="30"
                r="25"
                stroke="white"
                strokeWidth="4"
              />
              <path
                d="M18 30L26 38L42 22"
                stroke="white"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="m-0 w-full text-center text-lg font-semibold leading-[2] text-black">
            {title}
          </p>
        </div>
      </Dialog>
    );
  }

  if (size === "sm") {
    const palette = smConfirmPalettes[resolvedConfirmColor];
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth sx={dialogSx}>
        <DialogContent className="relative">
          <IconButton
            size="small"
            onClick={onClose}
            className="!absolute right-2 top-2"
            sx={{
              backgroundColor: "#ef4444",
              color: "#ffffff",
              "&:hover": { backgroundColor: "#dc2626" },
            }}
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <div
            className={`flex flex-col items-center px-2 text-center ${
              isSuccess ? "py-6" : "py-4"
            }`}
          >
            <div
              className={`mb-4 flex h-16 w-16 items-center justify-center rounded-full ${
                isSuccess ? "bg-green-500" : "bg-red-500"
              } text-white`}
            >
              {renderedIcon}
            </div>
            <h3 className="text-xl font-bold text-gray-950">{title}</h3>
            {message ? (
              <p className="mt-2 text-sm text-gray-600">{message}</p>
            ) : null}
            {isSuccess ? null : (
              <div className="mt-6 flex w-full gap-3">
                <Button
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  onClick={onConfirm}
                  startIcon={
                    loading ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : undefined
                  }
                  sx={{
                    backgroundColor: palette.bg,
                    textTransform: uppercaseActions ? "uppercase" : "none",
                    "&:hover": { backgroundColor: palette.hover },
                  }}
                >
                  {confirmLabel}
                </Button>
                {hideCancel ? null : (
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    onClick={onClose}
                    sx={{
                      backgroundColor: "#111827",
                      textTransform: uppercaseActions ? "uppercase" : "none",
                      "&:hover": { backgroundColor: "#000000" },
                    }}
                  >
                    {cancelLabel}
                  </Button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const palette = lgConfirmPalettes[resolvedConfirmColor];
  return (
    <Dialog open={open} onClose={onClose} sx={dialogSx}>
      <div
        className={`relative w-[560px] max-w-[calc(100vw-32px)] rounded bg-white px-12 text-center ${
          isSuccess ? "py-20" : "py-14"
        }`}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            backgroundColor: "#F31723",
            color: "white",
            position: "absolute",
            right: 16,
            top: 14,
            "&:hover": { backgroundColor: "#D90F1A" },
          }}
        >
          <CloseIcon />
        </IconButton>
        <div
          className={`mx-auto flex h-28 w-28 items-center justify-center rounded-full ${
            isSuccess ? "bg-[#16B983]" : "bg-[#F31723]"
          } text-white`}
        >
          {renderedIcon}
        </div>
        <div className="mt-12 text-[28px] font-extrabold leading-[1.65]">
          {title}
        </div>
        {message ? <p className="mt-4 text-sm text-gray-600">{message}</p> : null}
        {isSuccess ? null : (
          <div
            className={`mt-9 grid gap-6 ${
              hideCancel ? "mx-auto max-w-[230px]" : "sm:grid-cols-2"
            }`}
          >
            <Button
              disabled={loading}
              onClick={onConfirm}
              startIcon={
                loading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : undefined
              }
              sx={{
                backgroundColor: palette.bg,
                boxShadow: "0 4px 10px rgba(15, 23, 42, 0.22)",
                color: "white",
                fontSize: 17,
                fontWeight: 900,
                minHeight: 56,
                textTransform: uppercaseActions ? "uppercase" : "none",
                "&:hover": { backgroundColor: palette.hover },
              }}
            >
              {confirmLabel}
            </Button>
            {hideCancel ? null : (
              <Button
                disabled={loading}
                onClick={onClose}
                sx={{
                  backgroundColor: "#24272D",
                  color: "white",
                  fontSize: 17,
                  fontWeight: 900,
                  minHeight: 56,
                  textTransform: uppercaseActions ? "uppercase" : "none",
                  "&:hover": { backgroundColor: "#111827" },
                }}
              >
                {cancelLabel}
              </Button>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};
